import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Search, ShoppingBasket, Plus, Trash2, Sparkles, Calculator, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, imgUrl } from "@/lib/api";
import { PageHeader, Pager, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Basket() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [tab, setTab] = useState("catalog");
  const [profitFor, setProfitFor] = useState(null);

  const { data } = useQuery({
    queryKey: ["agent-catalog", page, search, category],
    queryFn: async () => (await api.get(`/agent/catalog?page=${page}&limit=18&search=${encodeURIComponent(search)}&category_id=${category}`)).data,
  });
  const { data: profit } = useQuery({
    queryKey: ["profit-calc", profitFor?.id],
    queryFn: async () => (await api.get(`/agent/products/${profitFor.id}/profit`)).data,
    enabled: !!profitFor,
  });

  const add = useMutation({
    mutationFn: async (pid) => (await api.post("/agent/basket", { product_id: pid })).data,
    onSuccess: (d) => { toast.success(d.already ? "Already in basket — synced to your store" : `Added to basket (${d.used}/${d.limit})`); ["agent-catalog", "agent-products", "my-store", "my-store-products"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: async (pid) => api.delete(`/agent/basket/${pid}`),
    onSuccess: () => { toast.success("Removed — swap in a new product anytime, free"); ["agent-catalog", "agent-products", "my-store", "my-store-products"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const syncStore = useMutation({
    mutationFn: async () => (await api.post("/agent/basket/sync-store", {})).data,
    onSuccess: (d) => { toast.success(d.synced ? `${d.synced} product(s) synced to your live store` : "Store already in sync"); ["agent-catalog", "agent-products", "my-store", "my-store-products"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const basket = data?.basket || { used: 0, limit: 0 };
  const items = (data?.items || []).filter((p) => (tab === "basket" ? p.in_basket : true));

  return (
    <div data-testid="basket-page">
      <PageHeader title="Product Basket" sub="Pick products from the IndiaFast catalog. Swap freely — removed products never count against your limit." testid="basket-title">
        <div data-testid="basket-usage" className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-center shadow-sm">
          <p className="text-lg font-extrabold text-[#0F2440]">{basket.used} <span className="text-sm font-medium text-slate-400">/ {basket.limit || "—"}</span></p>
          <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">{basket.plan ? `${basket.plan} plan` : "No plan"} products used</p>
        </div>
        {basket.not_in_store > 0 && (
          <button data-testid="store-sync-chip" onClick={() => syncStore.mutate()} disabled={syncStore.isPending}
            className="flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2 text-[11px] font-semibold text-amber-700 shadow-sm transition-colors hover:bg-amber-100">
            <RefreshCw size={11} className={syncStore.isPending ? "animate-spin" : ""} />
            {basket.not_in_store} not in store — Sync now
          </button>
        )}
      </PageHeader>

      {basket.limit > 0 && basket.used >= basket.limit && (
        <div data-testid="basket-full" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
          <b>Your basket is full ({basket.used}/{basket.limit}).</b> Remove a product to add another — swapping is always free.
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white p-1">
          {[["catalog", "Catalog"], ["basket", `My Basket (${basket.used})`]].map(([v, l]) => (
            <button key={v} data-testid={`basket-tab-${v}`} onClick={() => setTab(v)}
              className={`rounded-md px-4 py-1.5 text-xs font-semibold transition-colors ${tab === v ? "bg-[#FF6B00] text-white" : "text-slate-500"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="relative min-w-52 flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="catalog-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search products…" className="border-slate-200 bg-white pl-9 text-[#0F2440]" />
        </div>
        <Select value={category} onValueChange={(v) => { setCategory(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger data-testid="catalog-category" className="w-44 border-slate-200 bg-white text-[#0F2440]"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent className="border-slate-200 bg-white text-[#0F2440]">
            <SelectItem value="all">All categories</SelectItem>
            {(data?.categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!items.length ? (
        <EmptyState title={tab === "basket" ? "Your basket is empty" : "No products found"}
          sub={tab === "basket" ? "Browse the catalog and add your first product." : "Try a different search."} />
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6" data-testid="catalog-grid">
          {items.map((p) => (
            <div key={p.id} data-testid={`catalog-product-${p.sku}`}
              className="group overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm transition-shadow hover:shadow-lg">
              <div className="relative h-32 overflow-hidden bg-slate-50">
                {p.images?.[0] && <img src={imgUrl(p.images[0])} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}
                {p.in_basket && <span className="absolute left-2 top-2 rounded-md bg-[#138808] px-1.5 py-0.5 text-[9px] font-bold text-white">IN BASKET</span>}
                {p.available === 0 && <span className="absolute right-2 top-2 rounded-md bg-rose-500 px-1.5 py-0.5 text-[9px] font-bold text-white">OUT OF STOCK</span>}
              </div>
              <div className="p-3">
                <p className="truncate text-xs font-semibold text-[#0F2440]">{p.name}</p>
                <p className="font-mono text-[9px] text-slate-400">{p.sku}</p>
                <div className="mt-1.5 flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Cost {fmtINR(p.agent_cost)}</span>
                  <span className="font-bold text-[#138808]">+{fmtINR(p.estimated_profit)}</span>
                </div>
                <div className="mt-2 flex gap-1">
                  {p.in_basket ? (
                    <Button data-testid={`basket-remove-${p.sku}`} size="sm" variant="outline" onClick={() => remove.mutate(p.id)}
                      className="h-7 flex-1 border-rose-200 text-[10px] text-rose-600 hover:bg-rose-50"><Trash2 size={10} /> Remove</Button>
                  ) : (
                    <Button data-testid={`basket-add-${p.sku}`} size="sm" onClick={() => add.mutate(p.id)} disabled={p.available === 0}
                      className="h-7 flex-1 bg-[#FF6B00] text-[10px] font-bold text-white hover:bg-[#E85D04]"><Plus size={10} /> Add</Button>
                  )}
                  <Button data-testid={`profit-calc-${p.sku}`} size="sm" variant="outline" onClick={() => setProfitFor(p)}
                    className="h-7 border-slate-200 px-2 text-slate-500 hover:text-[#1D5BD8]"><Calculator size={11} /></Button>
                  <Button data-testid={`market-${p.sku}`} size="sm" variant="outline"
                    onClick={() => navigate(`/agent/ai-studio?product=${p.id}`)}
                    className="h-7 border-slate-200 px-2 text-slate-500 hover:text-violet-600"><Sparkles size={11} /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {data && tab === "catalog" && <Pager page={data.page} pages={data.pages} onPage={setPage} />}

      <Dialog open={!!profitFor} onOpenChange={() => setProfitFor(null)}>
        <DialogContent className="max-w-2xl border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>Profit Calculator — {profitFor?.name}</DialogTitle></DialogHeader>
          {profit && (
            <div className="space-y-3" data-testid="profit-calculator">
              <div className="grid gap-2 sm:grid-cols-2">
                {Object.entries(profit.channels).map(([ch, c]) => (
                  <div key={ch} data-testid={`profit-${ch}`} className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-bold">{c.label}</p>
                      <p className={`text-sm font-extrabold ${c.profit >= 0 ? "text-[#138808]" : "text-rose-600"}`}>{fmtINR(c.profit)} <span className="text-[10px] font-medium">({c.margin}%)</span></p>
                    </div>
                    <div className="mt-2 space-y-1 text-[11px] text-slate-600">
                      <div className="flex justify-between"><span>Your price</span><span className="font-semibold">{fmtINR(c.price)}</span></div>
                      <div className="flex justify-between"><span>Product cost</span><span>−{fmtINR(c.product_cost)}</span></div>
                      <div className="flex justify-between"><span>Fees (est. {c.fee_pct}%)</span><span>−{fmtINR(c.marketplace_fee)}</span></div>
                      <div className="flex justify-between"><span>Shipping ({profit.chargeable_kg ? `${profit.chargeable_kg} kg slab` : "flat"})</span><span>−{fmtINR(c.shipping)}</span></div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="rounded-lg bg-blue-50 px-3 py-2 text-[10px] leading-relaxed text-slate-500">{profit.note}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
